"use client";

import { COLOR, IllustrationCard } from "@/components/learn/chapter-1/_shared";
import { useDocLocale } from "@/components/shared/docs-blocks";
import type { Locale } from "@/lib/i18n";

const STRINGS: Record<
  Locale,
  {
    assets: Record<string, string>;
    drifted: string;
    whatChanged: string;
    marketOnly: string;
    lifeChanged: string;
    rebalance: string;
    backToDesign: string;
    targetUnchanged: string;
    whenLabel: string;
    rebalanceCase1: string;
    rebalanceCase2: string;
    redesign: string;
    reviewTarget: string;
    newTarget: string;
    redesignCase1: string;
    redesignCase2: string;
    title: string;
    description: string;
  }
> = {
  ja: {
    assets: { equity: "株式", bond: "債券", reit: "REIT", cash: "預金" },
    drifted: "配分がずれた",
    whatChanged: "何が変わった？",
    marketOnly: "相場が動いただけ",
    lifeChanged: "ライフスタイルが変わった",
    rebalance: "リバランス",
    backToDesign: "元の設計へ戻す",
    targetUnchanged: "目標配分（変えない）",
    whenLabel: "こんなとき",
    rebalanceCase1: "・株式が伸びて比率が上がった",
    rebalanceCase2: "・定期点検で許容幅を超えた",
    redesign: "リデザイン",
    reviewTarget: "目標配分を見直す",
    newTarget: "新しい目標配分（例）",
    redesignCase1: "・使う時期が近づいた／収入が変わった",
    redesignCase2: "・許容できる値動きが変わった",
    title: "戻す（リバランス）か、設計を変える（リデザイン）か",
    description:
      "相場の動きでずれただけなら目標配分へ戻します。目的や生活が変わったなら、目標配分そのものを見直してから整えます。",
  },
  en: {
    assets: { equity: "Equities", bond: "Bonds", reit: "REIT", cash: "Deposits" },
    drifted: "The allocation has drifted",
    whatChanged: "What changed?",
    marketOnly: "Only the market moved",
    lifeChanged: "Your circumstances changed",
    rebalance: "Rebalance",
    backToDesign: "Return to the original design",
    targetUnchanged: "Target allocation (unchanged)",
    whenLabel: "When to use it",
    rebalanceCase1: "· Equities grew and their weight rose",
    rebalanceCase2: "· A periodic check found it beyond your tolerance band",
    redesign: "Redesign",
    reviewTarget: "Revisit the target allocation",
    newTarget: "New target allocation (example)",
    redesignCase1: "· The date you need the money is closer, or your income changed",
    redesignCase2: "· The movement you can tolerate changed",
    title: "Return to the design (rebalance) or change the design (redesign)",
    description:
      "If it only drifted because the market moved, return to the target allocation. If your purpose or circumstances changed, revisit the target allocation itself before tidying up.",
  },
};

// ── Colors ─────────────────────────────────────────────────────────

const ASSET_COLOR = {
  equity: COLOR.sky,
  bond: COLOR.amber,
  reit: "#ec4899",
  cash: "#71717a",
};

type AssetKey = keyof typeof ASSET_COLOR;

type Allocation = { key: AssetKey; pct: number };

// ── Example allocations ────────────────────────────────────────────

const ORIGINAL_TARGET: Allocation[] = [
  { key: "equity", pct: 40 },
  { key: "bond", pct: 25 },
  { key: "reit", pct: 15 },
  { key: "cash", pct: 20 },
];

const REDESIGNED_TARGET: Allocation[] = [
  { key: "equity", pct: 25 },
  { key: "bond", pct: 45 },
  { key: "reit", pct: 10 },
  { key: "cash", pct: 20 },
];

// ── Layout ─────────────────────────────────────────────────────────

const VW = 560;
const VH = 288;

// ── Mini stacked bar ───────────────────────────────────────────────

function MiniStackedBar({
  x,
  y,
  w,
  h,
  allocations,
}: {
  x: number;
  y: number;
  w: number;
  h: number;
  allocations: Allocation[];
}) {
  let offset = 0;
  return (
    <g>
      {allocations.map((a) => {
        const segW = (a.pct / 100) * w;
        const segX = x + offset;
        offset += segW;
        return (
          <rect
            key={a.key}
            x={segX}
            y={y}
            width={segW}
            height={h}
            fill={ASSET_COLOR[a.key]}
            fillOpacity={0.65}
          />
        );
      })}
      <rect x={x} y={y} width={w} height={h} fill="none" stroke="currentColor" strokeOpacity={0.12} rx={2} />
    </g>
  );
}

// ── Allocation text summary ────────────────────────────────────────

function AllocationText({ x, y, allocations }: { x: number; y: number; allocations: Allocation[] }) {
  const t = STRINGS[useDocLocale()];
  return (
    <g>
      {allocations.map((a, i) => (
        <text
          key={a.key}
          x={x + i * 52}
          y={y}
          fontSize="9"
          fill={ASSET_COLOR[a.key]}
          fontWeight="700"
        >
          {t.assets[a.key]} {a.pct}%
        </text>
      ))}
    </g>
  );
}

// ── Main illustration ──────────────────────────────────────────────

function RebalanceVsRedesignIllustration() {
  const t = STRINGS[useDocLocale()];
  const cx = VW / 2;

  // Top pill: the allocation has drifted
  const topPillW = 140;
  const topPillX = cx - topPillW / 2;
  const topPillY = 8;
  const topPillH = 24;

  // Question text
  const questionY = 58;

  // Branch labels
  const branchLabelY = 90;

  // Outcome boxes
  const boxY = 108;
  const boxW = 244;
  const boxH = 162;
  const leftBoxX = 20;
  const rightBoxX = VW - 20 - boxW;

  const leftColor = COLOR.emerald;
  const rightColor = COLOR.sky;

  return (
    <svg viewBox={`0 0 ${VW} ${VH}`} className="mx-auto w-full max-w-2xl">
      <defs>
        <marker id="rvdArrow" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
          <path d="M 0 0 L 7 3.5 L 0 7 Z" fill="currentColor" fillOpacity={0.5} />
        </marker>
      </defs>

      {/* Top pill */}
      <rect
        x={topPillX}
        y={topPillY}
        width={topPillW}
        height={topPillH}
        rx={12}
        fill="currentColor"
        fillOpacity={0.06}
        stroke="currentColor"
        strokeOpacity={0.25}
      />
      <text x={cx} y={topPillY + 16} textAnchor="middle" fontSize="10.5" fill="currentColor" fontWeight="700">
        {t.drifted}
      </text>

      {/* Arrow down */}
      <line
        x1={cx}
        y1={topPillY + topPillH + 2}
        x2={cx}
        y2={questionY - 16}
        stroke="currentColor"
        strokeOpacity={0.35}
        markerEnd="url(#rvdArrow)"
      />

      {/* Question */}
      <text x={cx} y={questionY} textAnchor="middle" fontSize="11.5" fill="currentColor" fontWeight="800">
        {t.whatChanged}
      </text>

      {/* Diverging arrows */}
      <path
        d={`M ${cx} ${questionY + 6} Q ${cx - 60} ${branchLabelY - 4} ${leftBoxX + boxW / 2} ${boxY - 4}`}
        fill="none"
        stroke={leftColor}
        strokeOpacity={0.55}
        strokeWidth="1.2"
        markerEnd="url(#rvdArrow)"
      />
      <path
        d={`M ${cx} ${questionY + 6} Q ${cx + 60} ${branchLabelY - 4} ${rightBoxX + boxW / 2} ${boxY - 4}`}
        fill="none"
        stroke={rightColor}
        strokeOpacity={0.55}
        strokeWidth="1.2"
        markerEnd="url(#rvdArrow)"
      />

      {/* Branch labels */}
      <text x={cx - 100} y={branchLabelY} textAnchor="middle" fontSize="9.5" fill={leftColor} fontWeight="700">
        {t.marketOnly}
      </text>
      <text x={cx + 100} y={branchLabelY} textAnchor="middle" fontSize="9.5" fill={rightColor} fontWeight="700">
        {t.lifeChanged}
      </text>

      {/* ── Left box: rebalance ────────────────────────────────── */}
      <rect
        x={leftBoxX}
        y={boxY}
        width={boxW}
        height={boxH}
        rx={8}
        fill={leftColor}
        fillOpacity={0.05}
        stroke={leftColor}
        strokeOpacity={0.4}
      />
      {/* Header */}
      <rect x={leftBoxX} y={boxY} width={boxW} height={32} rx={8} fill={leftColor} fillOpacity={0.18} />
      <rect x={leftBoxX} y={boxY + 24} width={boxW} height={8} fill={leftColor} fillOpacity={0.18} />
      <text x={leftBoxX + 14} y={boxY + 20} fontSize="13" fill={leftColor} fontWeight="800">
        {t.rebalance}
      </text>
      <text x={leftBoxX + boxW - 14} y={boxY + 20} textAnchor="end" fontSize="8.5" fill={leftColor} fillOpacity={0.8} fontWeight="600">
        {t.backToDesign}
      </text>

      {/* Body: target allocation (unchanged) */}
      <text x={leftBoxX + 16} y={boxY + 54} fontSize="10" fill="currentColor" fillOpacity={0.6} fontWeight="700">
        {t.targetUnchanged}
      </text>
      <MiniStackedBar x={leftBoxX + 16} y={boxY + 62} w={boxW - 32} h={14} allocations={ORIGINAL_TARGET} />
      <AllocationText x={leftBoxX + 16} y={boxY + 92} allocations={ORIGINAL_TARGET} />

      {/* Body: when to use it */}
      <text x={leftBoxX + 16} y={boxY + 120} fontSize="10" fill="currentColor" fillOpacity={0.6} fontWeight="700">
        {t.whenLabel}
      </text>
      <text x={leftBoxX + 16} y={boxY + 138} fontSize="10.5" fill="currentColor">
        {t.rebalanceCase1}
      </text>
      <text x={leftBoxX + 16} y={boxY + 154} fontSize="10.5" fill="currentColor">
        {t.rebalanceCase2}
      </text>

      {/* ── Right box: redesign ───────────────────────────────── */}
      <rect
        x={rightBoxX}
        y={boxY}
        width={boxW}
        height={boxH}
        rx={8}
        fill={rightColor}
        fillOpacity={0.05}
        stroke={rightColor}
        strokeOpacity={0.4}
      />
      {/* Header */}
      <rect x={rightBoxX} y={boxY} width={boxW} height={32} rx={8} fill={rightColor} fillOpacity={0.18} />
      <rect x={rightBoxX} y={boxY + 24} width={boxW} height={8} fill={rightColor} fillOpacity={0.18} />
      <text x={rightBoxX + 14} y={boxY + 20} fontSize="13" fill={rightColor} fontWeight="800">
        {t.redesign}
      </text>
      <text x={rightBoxX + boxW - 14} y={boxY + 20} textAnchor="end" fontSize="8.5" fill={rightColor} fillOpacity={0.8} fontWeight="600">
        {t.reviewTarget}
      </text>

      {/* Body: new target allocation */}
      <text x={rightBoxX + 16} y={boxY + 54} fontSize="10" fill="currentColor" fillOpacity={0.6} fontWeight="700">
        {t.newTarget}
      </text>
      <MiniStackedBar x={rightBoxX + 16} y={boxY + 62} w={boxW - 32} h={14} allocations={REDESIGNED_TARGET} />
      <AllocationText x={rightBoxX + 16} y={boxY + 92} allocations={REDESIGNED_TARGET} />

      {/* Body: when to use it */}
      <text x={rightBoxX + 16} y={boxY + 120} fontSize="10" fill="currentColor" fillOpacity={0.6} fontWeight="700">
        {t.whenLabel}
      </text>
      <text x={rightBoxX + 16} y={boxY + 138} fontSize="10.5" fill="currentColor">
        {t.redesignCase1}
      </text>
      <text x={rightBoxX + 16} y={boxY + 154} fontSize="10.5" fill="currentColor">
        {t.redesignCase2}
      </text>
    </svg>
  );
}

// ── Export ──────────────────────────────────────────────────────────

export function DocRebalanceVsRedesignDiagram() {
  const t = STRINGS[useDocLocale()];
  return (
    <IllustrationCard
      title={t.title}
      description={t.description}
    >
      <RebalanceVsRedesignIllustration />
    </IllustrationCard>
  );
}
